// What the game bar says about a game, worked out from its stored record and
// PGN headers: the players with their ratings, the result, how it ended, and
// the particulars behind "More". Pure, so it can be tested without a review.

import { type Color, type Move, type GameRecord } from '@chess/api-model';
import type { Fact, GameSummary } from './GameBar.tsx';

export function pgnHeaders(pgn: string): Record<string, string> {
  const headers: Record<string, string> = {};
  const pattern = /^\[(\w+)\s+"((?:[^"\\]|\\.)*)"\]\s*$/gm;
  for (let m = pattern.exec(pgn); m; m = pattern.exec(pgn)) headers[m[1]] = m[2];
  return headers;
}

const player = (p: { name: string; rating?: number }) => (p.rating ? `${p.name} (${p.rating})` : p.name);

/** "White won by resignation", "Drawn by repetition", from the Termination header, else the result. */
export function outcomeText(record: Pick<GameRecord, 'white' | 'black' | 'result'>, termination?: string): string {
  const t = termination?.trim();
  if (t) {
    const lower = t.toLowerCase();
    const white = record.white.name.toLowerCase();
    const black = record.black.name.toLowerCase();
    if (lower.startsWith(`${white} won`)) return `White won${t.slice(white.length + 4)}`;
    if (lower.startsWith(`${black} won`)) return `Black won${t.slice(black.length + 4)}`;
    if (lower.startsWith('game drawn')) return `Drawn${t.slice('game drawn'.length)}`;
    if (lower.startsWith('game abandoned')) return 'Abandoned';
  }
  switch (record.result) {
    case '1-0':
      return 'White won';
    case '0-1':
      return 'Black won';
    case '1/2-1/2':
      return 'Drawn';
    default:
      return 'Unfinished';
  }
}

/** "5+0" -> "5 + 0", "daily" -> "Daily". */
export function clockText(timeControl?: string): string | undefined {
  if (!timeControl) return undefined;
  if (timeControl === 'daily') return 'Daily';
  const [base, inc = '0'] = timeControl.split('+');
  return `${base} + ${inc}`;
}

/** Chess.com's live classes by expected duration, base plus forty increments: it has no classical. */
export function timeClass(timeControl?: string): string | undefined {
  if (!timeControl) return undefined;
  if (timeControl === 'daily') return 'Daily';
  const [base, inc = '0'] = timeControl.split('+');
  const baseSeconds = base.endsWith('s') ? Number(base.slice(0, -1)) : Number(base) * 60;
  if (!Number.isFinite(baseSeconds)) return undefined;
  const seconds = baseSeconds + 40 * Number(inc || 0);
  if (seconds < 180) return 'Bullet';
  if (seconds < 600) return 'Blitz';
  return 'Rapid';
}

/** How long the game took, from chess.com's UTC start and end headers. */
export function tookText(headers: Record<string, string>): string | undefined {
  const startDate = headers.UTCDate ?? headers.Date;
  const endDate = headers.EndDate ?? startDate;
  const start = headers.UTCTime ?? headers.StartTime;
  const end = headers.EndTime;
  if (!startDate || !start || !end) return undefined;
  const iso = (d: string, t: string) => `${d.replace(/\./g, '-')}T${t}Z`;
  const ms = Date.parse(iso(endDate, end)) - Date.parse(iso(startDate, start));
  if (!Number.isFinite(ms) || ms < 0) return undefined;
  const minutes = Math.round(ms / 60000);
  if (minutes < 1) return `${Math.round(ms / 1000)} s`;
  if (minutes < 120) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours} h` : `${Math.round(hours / 24)} days`;
}

/** "14 Sep 2026, 21:07" in the reader's own time zone. */
export function playedText(playedAt: string): string | undefined {
  const d = new Date(playedAt);
  if (Number.isNaN(d.getTime())) return undefined;
  const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${date}, ${time}`;
}

/** The opening's name from chess.com's ECOUrl, without the moves; else the Opening header. */
export function openingFromHeaders(headers: Record<string, string>): string | undefined {
  const url = headers.ECOUrl;
  if (url) {
    const slug = url.split('/').filter(Boolean).pop() ?? '';
    const words: string[] = [];
    for (const w of slug.split('-')) {
      if (/^\d+\.{1,3}/.test(w) || /^[KQRBN]?[a-h]?x?[a-h][1-8]/.test(w) || /^O(-O)*$/.test(w)) break;
      words.push(w);
    }
    if (words.length) return words.join(' ');
  }
  return headers.Opening || undefined;
}

const SOURCE_NAME: Record<string, string> = { 'chess.com': 'chess.com', lichess: 'Lichess', local: 'this app' };

/**
 * The bar's summary. `moveCount` is the game's plies; `opening` is the book's
 * name for the game when the review has it, preferred over the PGN's.
 */
export function summaryOf(record: GameRecord, moveCount: number, opening?: string): GameSummary {
  const headers = pgnHeaders(record.pgn);
  const facts: Fact[] = [];
  const add = (label: string, value: string | undefined) => {
    if (value) facts.push({ label, value });
  };
  add('Played', playedText(record.playedAt));
  add('Clock', clockText(record.timeControl));
  add('Type', timeClass(record.timeControl));
  add('Took', tookText(headers));
  add('Opening', opening ?? openingFromHeaders(headers));
  add('Moves', moveCount > 0 ? String(Math.ceil(moveCount / 2)) : undefined);
  add('From', SOURCE_NAME[record.source] ?? record.source);
  return {
    white: player(record.white),
    black: player(record.black),
    resultText: record.result,
    outcomeText: outcomeText(record, headers.Termination),
    facts,
  };
}

/** The part of a game's scan the blunder count reads: a move's judgement at a 1-based ply. */
export interface BlunderScan {
  /** Blunder, miss or neither for a move, by the review API's rule (`GameScan.kind`). */
  kind(ply: number, move: Move | undefined, mover: Color): 'blunder' | 'miss' | undefined;
}

/**
 * The reader's own blunders in the game, from its scan: moves at the
 * reader's plies (odd plies are White's) that are blunders by the review
 * API's rule; a missed win that kept the position is a miss, not counted. Undefined while
 * there is no scan or the owner played neither side, so the bar shows no
 * count rather than a misleading zero.
 */
export function ownBlunders(scan: BlunderScan | undefined, moves: Move[], reader: Color | undefined): number | undefined {
  if (!scan || !reader) return undefined;
  let count = 0;
  moves.forEach((move, i) => {
    const ply = i + 1;
    if ((ply % 2 === 1 ? 'white' : 'black') !== reader) return;
    if (scan.kind(ply, move, reader) === 'blunder') count++;
  });
  return count;
}
